import React, { useEffect, useState } from "react";
import moment from "moment";
import { useMutation, gql } from "@apollo/client";

const ADD_BLOCKED_SLOTS = gql`
  mutation addBlockedSlots($data: BlockedSlotsInput) {
    addBlockedSlots(data: $data) {
      status
      message
    }
  }
`;

const TimeSlotSelector = ({
  employeeId,
  fetchBlockedSlots,
  gender,
  selectedDate,
  setSelectedDate,
}) => {
  const [weekDays, setWeekDays] = useState([]);
  const [blockedSlotsMap, setBlockedSlotsMap] = useState({});
  const [reason, setReason] = useState("");
  const [selectedRange, setSelectedRange] = useState([]);

  const allSlots = generateTimeSlots(
    gender === "Female" ? "08:00" : "08:00",
    gender === "Female" ? "18:00" : "18:00"
  );

  const [addBlockedSlotsApi, { loading: submitLoading }] =
    useMutation(ADD_BLOCKED_SLOTS);

  function generateTimeSlots(start, end) {
    const slots = [];
    let time = moment(start, "HH:mm");
    const endTime = moment(end, "HH:mm");
    while (time.isSameOrBefore(endTime)) {
      slots.push(time.format("HH:mm"));
      time.add(30, "minutes");
    }
    return slots;
  }

  const fetchBlockedSlotsForDay = async ({ employeeId, date }) => {
    const res = await fetchBlockedSlots({ employeeId, date });

    const blocked = [];
    (res?.blockedSlots || []).forEach(({ startSlot, endSlot }) => {
      const start = moment(startSlot, "HH:mm");
      const end = moment(endSlot, "HH:mm");
      while (start.isSameOrBefore(end)) {
        blocked.push(start.format("HH:mm"));
        start.add(30, "minutes");
      }
    });

    (res?.bookedSlots || []).forEach(({ startSlot, endSlot }) => {
      const start = moment(startSlot, "HH:mm");
      const end = moment(endSlot, "HH:mm");
      while (start.isSameOrBefore(end)) {
        blocked.push(start.format("HH:mm"));
        start.add(30, "minutes");
      }
    });

    return blocked;
  };

  useEffect(() => {
    const days = Array.from({ length: 7 }, (_, i) =>
      moment().add(i, "days").format("YYYY-MM-DD")
    );
    setWeekDays(days);

    const fetchAll = async () => {
      const result = {};
      for (const day of days) {
        const blocked = await fetchBlockedSlotsForDay({
          employeeId,
          date: day,
        });
        result[day] = blocked;
      }
      setBlockedSlotsMap(result);
    };

    if (employeeId && fetchBlockedSlots) {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const handleSubmit = async () => {
    if (selectedRange.length < 2 || !reason.trim()) {
      alert("Please select a valid time range and enter a reason.");
      return;
    }

    const payload = {
      employeeId,
      date: selectedDate,
      startSlot: selectedRange[0],
      endSlot: selectedRange[selectedRange.length - 1],
      reason: reason.trim(),
    };

    try {
      await addBlockedSlotsApi({ variables: { data: payload } });

      const blocked = await fetchBlockedSlotsForDay({
        employeeId,
        date: selectedDate,
      });

      alert("Blocked slot submitted successfully.");
      setSelectedRange([]);
      setReason("");

      setBlockedSlotsMap((prev) => ({
        ...prev,
        [selectedDate]: blocked,
      }));
    } catch (err) {
      console.error("Error submitting blocked slot:", err);
      alert("Failed to submit blocked slot.");
    }
  };

  function handleSlotClick(slot) {
    const currentRange = [...selectedRange];
    if (currentRange.length === 0) {
      setSelectedRange([slot]);
    } else if (currentRange.length === 1) {
      const first = moment(currentRange[0], "HH:mm");
      const second = moment(slot, "HH:mm");

      const start = moment.min(first, second);
      const end = moment.max(first, second);
      const diff = end.diff(start, "minutes");

      if (diff % 30 === 0 && diff > 0) {
        const steps = diff / 30;
        const fullRange = Array.from({ length: steps + 1 }, (_, i) =>
          start
            .clone()
            .add(i * 30, "minutes")
            .format("HH:mm")
        );

        const blocked = blockedSlotsMap[selectedDate] || [];
        const hasBlocked = fullRange.some((s) => blocked.includes(s));

        if (!hasBlocked) {
          setSelectedRange(fullRange);
        } else {
          // If range contains blocked slot, treat second click as new start
          setSelectedRange([slot]);
        }
      } else {
        setSelectedRange([slot]);
      }
    } else {
      setSelectedRange([slot]);
    }
  }

  const renderSlot = (slot, date) => {
    const isBlocked = blockedSlotsMap[date]?.includes(slot);
    const isSelected = selectedRange.includes(slot);

    return (
      <button
        key={slot}
        disabled={isBlocked}
        onClick={() => handleSlotClick(slot)}
        className={`px-2 py-1 m-1 rounded-md border text-xs sm:text-sm transition-colors
          ${
            isBlocked
              ? "bg-gray-100 text-gray-500 border-gray-300 cursor-not-allowed"
              : isSelected
              ? "bg-[#7F58E6] text-white border-[#7F58E6]"
              : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
          }`}
      >
        {slot}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">
        Select Time Slots to Block
      </h3>

      {/* Date chips */}
      <div className="flex flex-wrap gap-2 mb-2">
        {weekDays.map((day) => (
          <button
            key={day}
            onClick={() => {
              setSelectedDate(day);
              setSelectedRange([]);
            }}
            className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-colors
              ${
                selectedDate === day
                  ? "bg-[#7F58E6] text-white border-[#7F58E6]"
                  : "bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100"
              }`}
          >
            {moment(day).format("ddd, MMM D")}
          </button>
        ))}
      </div>

      {/* Slots grid */}
      <div className="flex flex-wrap max-w-xl">
        {allSlots.map((slot) => renderSlot(slot, selectedDate))}
      </div>

      {/* Range + reason + submit */}
      {selectedRange.length > 1 && (
        <div className="mt-4 space-y-3 max-w-md">
          <div className="text-xs sm:text-sm text-emerald-300">
            Selected:{" "}
            <span className="font-semibold">
              {selectedRange[0]} – {selectedRange[selectedRange.length - 1]}
            </span>
          </div>

          <textarea
            rows={3}
            placeholder="Enter reason for blocking"
            className="w-full p-2 rounded-lg bg-gray-50 border border-gray-300 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F58E6]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          <button
            className="inline-flex items-center justify-center bg-[#7F58E6] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#e4a32b] disabled:opacity-60"
            onClick={handleSubmit}
            disabled={submitLoading}
          >
            {submitLoading ? "Submitting..." : "Submit Blocked Slot"}
          </button>
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;
